import { Injectable, computed, signal } from '@angular/core';

import type { DashboardCardItem, DashboardChartItem, DashboardChartSourceKey, DashboardMetricKey, DashboardPreset } from '../types';

export interface PaneDragGhost {
  readonly label: string;
  readonly typeIcon: string;
}

export type PaneDragSource =
  | {
      readonly kind: 'widget-pane';
      readonly widgetId: string;
      readonly itemIndex: number;
      readonly item: DashboardCardItem;
    }
  | {
      readonly kind: 'palette-chart-pane';
      readonly item: DashboardChartItem;
    }
  | {
      readonly kind: 'palette-metric';
      readonly metricKey: DashboardMetricKey | string;
    }
  | {
      readonly kind: 'palette-text';
    }
  | {
      readonly kind: 'palette-preset';
    }
  | {
      readonly kind: 'palette-blank';
    }
  | {
      readonly kind: 'palette-table';
    }
  | {
      readonly kind: 'palette-linked';
    }
  | {
      readonly kind: 'palette-direct';
      readonly item: DashboardCardItem;
      readonly colSpan: number;
      readonly rowSpan: number;
    }
  | {
      readonly kind: 'palette-preset-direct';
      readonly preset: DashboardPreset;
    }
  | {
      readonly kind: 'canvas-card';
      readonly widgetId: string;
      readonly colSpan: number;
      readonly rowSpan: number;
    };

export type PaneDropTarget =
  | {
      readonly kind: 'widget-pane-slot';
      readonly widgetId: string;
      readonly insertIndex: number;
      readonly currentItemCount: number;
      readonly maxItemCount?: number;
    }
  | {
      readonly kind: 'canvas-empty';
      readonly x: number;
      readonly y: number;
      readonly colSpan: number;
      readonly rowSpan: number;
    }
  | {
      readonly kind: 'cancel';
    };

export interface PaneDragRequest {
  readonly source: PaneDragSource;
  readonly ghost: PaneDragGhost;
  readonly pointer: { readonly x: number; readonly y: number };
  readonly defaultSize: { readonly colSpan: number; readonly rowSpan: number };
}

export type PaneDropCommitHandler = (target: PaneDropTarget, source: PaneDragSource) => void;

@Injectable({ providedIn: 'root' })
export class DashboardPaneDragService {
  private readonly _active = signal<PaneDragRequest | null>(null);
  private readonly _target = signal<PaneDropTarget | null>(null);
  private readonly _pointer = signal<{ x: number; y: number } | null>(null);
  private commitHandler: PaneDropCommitHandler | null = null;
  private readonly hoveredWidgets = new Set<string>();

  readonly active = this._active.asReadonly();
  readonly target = this._target.asReadonly();
  readonly pointer = this._pointer.asReadonly();

  readonly isDragging = computed<boolean>(() => this._active() !== null);

  readonly defaultSize = computed<{ colSpan: number; rowSpan: number } | null>(() => {
    const drag = this._active();
    return drag ? drag.defaultSize : null;
  });

  isSourcePane(widgetId: string, itemIndex: number): boolean {
    const drag = this._active();
    if (!drag || drag.source.kind !== 'widget-pane') return false;
    return drag.source.widgetId === widgetId && drag.source.itemIndex === itemIndex;
  }

  isTargetWidget(widgetId: string): boolean {
    const t = this._target();
    return !!t && t.kind === 'widget-pane-slot' && t.widgetId === widgetId;
  }

  isTargetSlot(widgetId: string, insertIndex: number): boolean {
    const t = this._target();
    return (
      !!t &&
      t.kind === 'widget-pane-slot' &&
      t.widgetId === widgetId &&
      t.insertIndex === insertIndex
    );
  }

  setCommitHandler(handler: PaneDropCommitHandler | null): void {
    this.commitHandler = handler;
  }

  begin(request: PaneDragRequest): void {
    this._active.set(request);
    this._target.set(null);
    this._pointer.set({ ...request.pointer });
    this.attachDocListeners();
  }

  updatePointer(x: number, y: number): void {
    if (!this._active()) return;
    this._pointer.set({ x, y });
  }

  setTarget(target: PaneDropTarget | null): void {
    if (!this._active()) return;
    this._target.set(target);
  }

  clearTargetIfMatches(predicate: (target: PaneDropTarget) => boolean): void {
    const t = this._target();
    if (t && predicate(t)) this._target.set(null);
  }

  readonly isCancelTarget = computed<boolean>(() => this._target()?.kind === 'cancel');

  isValidDrop(): boolean {
    const drag = this._active();
    const target = this._target();
    if (!drag || !target) return false;
    if (target.kind === 'cancel') return false;
    if (
      target.kind === 'widget-pane-slot' &&
      (drag.source.kind === 'palette-preset' ||
       drag.source.kind === 'palette-blank' ||
       drag.source.kind === 'canvas-card')
    ) return false;
    if (target.kind === 'widget-pane-slot') {
      if (
        drag.source.kind === 'widget-pane' &&
        drag.source.widgetId === target.widgetId &&
        (target.insertIndex === drag.source.itemIndex ||
          target.insertIndex === drag.source.itemIndex + 1)
      ) {
        return false;
      }
      const isSameWidget =
        drag.source.kind === 'widget-pane' && drag.source.widgetId === target.widgetId;
      const wouldGrow = !isSameWidget;
      if (wouldGrow && target.currentItemCount >= (target.maxItemCount ?? 3)) return false;
    }
    return true;
  }

  commit(): boolean {
    const drag = this._active();
    const target = this._target();
    if (drag && target && this.commitHandler && this.isValidDrop()) {
      this.stopAutoScroll();
      this.commitHandler(target, drag.source);
      this.cancel();
      return true;
    }
    this.cancel();
    return false;
  }

  cancel(): void {
    this.stopAutoScroll();
    this._active.set(null);
    this._target.set(null);
    this._pointer.set(null);
    this.hoveredWidgets.clear();
    this.detachDocListeners();
  }

  notifyWidgetEnter(widgetId: string): void {
    this.hoveredWidgets.add(widgetId);
  }

  notifyWidgetLeave(widgetId: string): void {
    this.hoveredWidgets.delete(widgetId);
  }

  get isHoveringWidget(): boolean {
    return this.hoveredWidgets.size > 0;
  }

  private docListenersAttached = false;
  private autoScrollRaf: number | null = null;
  private lastPointerClientY = 0;

  private readonly docMoveHandler = (e: PointerEvent): void => {
    this.lastPointerClientY = e.clientY;
    this.updatePointer(e.clientX, e.clientY);
    this.ensureAutoScroll();
  };

  private readonly docUpHandler = (): void => {
    this.commit();
  };

  private readonly docKeyHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.cancel();
  };

  private scrollContainer: HTMLElement = document.documentElement;

  registerScrollContainer(el: HTMLElement): void {
    this.scrollContainer = el;
  }

  private ensureAutoScroll(): void {
    if (this.autoScrollRaf !== null) return;
    const scrollEl = this.scrollContainer;
    const tick = (): void => {
      if (!this._active()) {
        this.autoScrollRaf = null;
        return;
      }
      const edge = 56;
      const maxSpeed = 18;
      const fromBottom = window.innerHeight - this.lastPointerClientY;
      const fromTop = this.lastPointerClientY;
      let speed = 0;
      if (fromBottom < edge) speed = maxSpeed * (1 - Math.max(0, fromBottom) / edge);
      else if (fromTop < edge) speed = -maxSpeed * (1 - Math.max(0, fromTop) / edge);
      if (speed !== 0) {
        const maxScrollTop = scrollEl.scrollHeight - scrollEl.clientHeight;
        scrollEl.scrollTop = Math.max(0, Math.min(scrollEl.scrollTop + speed, maxScrollTop));
      }
      this.autoScrollRaf = requestAnimationFrame(tick);
    };
    this.autoScrollRaf = requestAnimationFrame(tick);
  }

  private stopAutoScroll(): void {
    if (this.autoScrollRaf === null) return;
    cancelAnimationFrame(this.autoScrollRaf);
    this.autoScrollRaf = null;
  }

  private attachDocListeners(): void {
    if (this.docListenersAttached) return;
    document.addEventListener('pointermove', this.docMoveHandler);
    document.addEventListener('pointerup', this.docUpHandler);
    document.addEventListener('pointercancel', this.docUpHandler);
    document.addEventListener('keydown', this.docKeyHandler);
    this.docListenersAttached = true;
  }

  private detachDocListeners(): void {
    if (!this.docListenersAttached) return;
    document.removeEventListener('pointermove', this.docMoveHandler);
    document.removeEventListener('pointerup', this.docUpHandler);
    document.removeEventListener('pointercancel', this.docUpHandler);
    document.removeEventListener('keydown', this.docKeyHandler);
    this.docListenersAttached = false;
  }
}
